import { Injectable } from '@angular/core';
import { ItemAttribute } from '../../models/item';
import { ItemAttributesService } from '../services/item-attributes.service';
import { ComponentStore } from '@ngrx/component-store';
import {  tapResponse } from '@ngrx/operators';
import { pipe, switchMap, withLatestFrom } from 'rxjs';
import { tap } from 'rxjs/operators';

interface ItemAttributesState {
  itemId: number;
  attributes: ItemAttribute[];
  loading: boolean;
  error: any;
  updatingAttributeValue: ItemAttribute | null;
  editingAttribute: ItemAttribute | null;
  view: 'list' | 'addValue' | 'create' | 'edit';
}

const initialState: ItemAttributesState = {
  itemId: null,
  attributes: [],
  loading: false,
  error: null,
  updatingAttributeValue: null,
  editingAttribute: null,
  view: 'list'
};

@Injectable()
export class ItemAttributesStoreService extends ComponentStore<ItemAttributesState> {
  constructor(private itemAttributeService: ItemAttributesService) {
    super(initialState);
  }

  private readonly setItemId = this.updater((state, itemId: number) => ({
    ...state,
    itemId
  }));

  readonly attributes$ = this.select((state) => state.attributes);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly view$ = this.select((state) => state.view);
  readonly editingAttribute$ = this.select((state) => state.editingAttribute);
  updateAttribute$ = this.select((state) => state.updatingAttributeValue);

  readonly loadAttributes = this.effect<number>(
    pipe(
      tap(() =>
        this.patchState({ loading: true, updatingAttributeValue: null })
      ),
      switchMap((itemId) =>
        this.itemAttributeService.getAll(itemId).pipe(
          tapResponse(
            (attributes) =>
              this.patchState({ attributes, loading: false, view: 'list' }),
            (error) => this.patchState({ error, loading: false })
          )
        )
      )
    )
  );

  readonly addAttribute = this.effect<ItemAttributeCreatePayload>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      withLatestFrom(this.select((state) => state.itemId)),
      switchMap(([attribute, itemId]) =>
        this.itemAttributeService.create(itemId, attribute).pipe(
          tapResponse(
            (attribute) => this.loadAttributes(itemId),
            (error) => this.patchState({ error, loading: false })
          )
        )
      )
    )
  );

  readonly updateAttribute = this.effect<Partial<ItemAttribute>>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      withLatestFrom(this.select((state) => state.editingAttribute)),
      switchMap(([attribute, { item_id, id }]) =>
        this.itemAttributeService.update(item_id, id, attribute).pipe(
          tapResponse(
            (attribute) => this.loadAttributes(item_id),
            (error) => this.patchState({ error, loading: false })
          )
        )
      )
    )
  );

  readonly setAttributeValue = this.effect<{
    attributeId: number;
    value: string;
  }>(
    pipe(
      tap(() => this.patchState({ loading: true })),
      withLatestFrom(this.select((state) => state.itemId)),
      switchMap(([{ attributeId, value }, itemId]) =>
        this.itemAttributeService.setValue(itemId, attributeId, value).pipe(
          tapResponse(
            () => this.loadAttributes(itemId),
            (error) => this.patchState({ error, loading: false })
          )
        )
      )
    )
  );

  readonly switchToUpdateValueView = this.updater(
    (state, updatingAttribute: ItemAttribute) => ({
      ...state,
      view: 'addValue',
      updatingAttributeValue: updatingAttribute
    })
  );

  readonly switchToEditAttributeView = this.updater(
    (state, editingAttribute: ItemAttribute) => ({
      ...state,
      view: 'edit',
      editingAttribute
    })
  );

  readonly setView = this.updater(
    (state, view: 'list' | 'create' | 'addValue') => ({
      ...state,
      view
    })
  );

  setItemIdAndLoadAttributes(itemId: number) {
    this.setItemId(itemId);
    this.loadAttributes(itemId);
  }

  deleteAttribute(attribute: ItemAttribute) {
    this.itemAttributeService.delete(attribute.item_id, attribute.id).subscribe(
      () => {
        this.loadAttributes(attribute.item_id);
      },
      (error) => {
        this.patchState({ error });
      }
    );
  }
}

export type ItemAttributeCreatePayload = Omit<
  ItemAttribute,
  | 'id'
  | 'latest_value'
  | 'item_id'
  | 'created_at'
  | 'updated_at'
  | 'deleted_at'
  | 'current_value'
>;
